/** UI-free dispatch from an `AdjustmentConfig` to the matching
 *  `applyXxxToCanvas` math. Lives separately from `registry.ts`
 *  because the registry pulls in React Panel components and we
 *  want the slow-path compositor in `render-pipeline.ts` to stay
 *  free of UI deps (and free of the cost of pulling React into
 *  any non-UI consumer's bundle / test).
 *
 *  Adding an implemented kind = entry in the switch below + entry
 *  in the registry's `ADJUSTMENT_SPECS`. The two stay aligned by
 *  type — `AdjustmentConfig['kind']` is the discriminator both
 *  sides switch on. */
import type { AdjustmentConfig } from '../../types/image-editor.types';
import { applyAddNoiseToCanvas, type AddNoiseValue } from '../filters/add-noise';
import { applyBoxBlurToCanvas, type BoxBlurValue } from '../filters/box-blur';
import {
  applyChromaticAberrationToCanvas,
  type ChromaticAberrationValue,
} from '../filters/chromatic-aberration';
import { applyEmbossToCanvas, type EmbossValue } from '../filters/emboss';
import { applyFindEdgesToCanvas, type FindEdgesValue } from '../filters/find-edges';
import { applyGaussianBlurToCanvas, type GaussianBlurValue } from '../filters/gaussian-blur';
import { applyLensCorrectionToCanvas, type LensCorrectionValue } from '../filters/lens-correction';
import { applyMedianToCanvas, type MedianValue } from '../filters/median';
import { applyMotionBlurToCanvas, type MotionBlurValue } from '../filters/motion-blur';
import { applyPinchToCanvas, type PinchValue } from '../filters/pinch';
import { applyPixelateToCanvas, type PixelateValue } from '../filters/pixelate';
import { applyPosterizeToCanvas, type PosterizeValue } from '../filters/posterize';
import { applyRadialBlurToCanvas, type RadialBlurValue } from '../filters/radial-blur';
import { applyReduceNoiseToCanvas, type ReduceNoiseValue } from '../filters/reduce-noise';
import { applySmartSharpenToCanvas, type SmartSharpenValue } from '../filters/smart-sharpen';
import { applyThresholdToCanvas, type ThresholdValue } from '../filters/threshold';
import { applyTwirlToCanvas, type TwirlValue } from '../filters/twirl';
import { applyUnsharpMaskToCanvas, type UnsharpMaskValue } from '../filters/unsharp-mask';
import { applyVignetteToCanvas, type VignetteValue } from '../filters/vignette';
import { applyAdjustmentOnGpu, hasGpuImplementation } from '../gpu/dispatch';
import { applyBlackAndWhiteToCanvas, type BlackAndWhiteValue } from './black-white';
import {
  applyBrightnessContrastToCanvas,
  type BrightnessContrastValue,
} from './brightness-contrast';
import { applyColorBalanceToCanvas, type ColorBalanceValue } from './color-balance';
import { applyCurvesToCanvas, type CurvesValue } from './curves';
import { applyHueSaturationToCanvas, type HueSaturationValue } from './hue-saturation';
import { applyLevelsToCanvas, type LevelsValue } from './levels';
import { applyShadowsHighlightsToCanvas, type ShadowsHighlightsValue } from './shadows-highlights';
import { applyVibranceToCanvas, type VibranceValue } from './vibrance';

/** Apply an adjustment config to a target canvas, reading source
 *  pixels from a captured `ImageData`. Non-cumulative: calling
 *  twice with the same config produces the same pixels — the apply
 *  functions all read from `source` and write to `target` rather
 *  than reading the target.
 *
 *  Returns `true` when the kind matched an implemented dispatch,
 *  `false` for placeholder kinds (`selective-color`, `invert`,
 *  etc.) whose math hasn't shipped yet. Callers should treat a
 *  `false` return as "nothing changed" — leaves the target as-is.
 *  Adjustment-layer creation paths gate on `isImplementedKind`
 *  upfront so a placeholder never reaches this code in production.
 *
 *  GPU-first: kinds with a shader implementation in `lib/gpu/` run
 *  on the WebGL2 path (cf. `lib/gpu/runtime.ts`). On any GPU failure
 *  — context unavailable, shader compile error, texture upload
 *  failure — we fall through to the CPU switch below. The CPU
 *  switch remains the authoritative reference implementation; GPU
 *  shaders must produce byte-equivalent results per parity tests. */
export function applyAdjustmentToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  config: AdjustmentConfig,
): boolean {
  if (hasGpuImplementation(config.kind)) {
    if (applyAdjustmentOnGpu(source, target, config)) return true;
    // GPU path failed — fall through to the CPU dispatch.
  }
  switch (config.kind) {
    case 'black-white':
      applyBlackAndWhiteToCanvas(source, target, config);
      return true;
    case 'brightness-contrast':
      applyBrightnessContrastToCanvas(source, target, config);
      return true;
    case 'color-balance':
      applyColorBalanceToCanvas(source, target, config);
      return true;
    case 'curves':
      applyCurvesToCanvas(source, target, config);
      return true;
    case 'hue-saturation':
      applyHueSaturationToCanvas(source, target, config);
      return true;
    case 'levels':
      applyLevelsToCanvas(source, target, config);
      return true;
    case 'shadows-highlights':
      applyShadowsHighlightsToCanvas(source, target, config);
      return true;
    case 'vibrance':
      applyVibranceToCanvas(source, target, config);
      return true;
    case 'filter-gaussian-blur':
      applyGaussianBlurToCanvas(source, target, config);
      return true;
    case 'filter-box-blur':
      applyBoxBlurToCanvas(source, target, config);
      return true;
    case 'filter-motion-blur':
      applyMotionBlurToCanvas(source, target, config);
      return true;
    case 'filter-radial-blur':
      applyRadialBlurToCanvas(source, target, config);
      return true;
    case 'filter-unsharp-mask':
      applyUnsharpMaskToCanvas(source, target, config);
      return true;
    case 'filter-smart-sharpen':
      applySmartSharpenToCanvas(source, target, config);
      return true;
    case 'filter-add-noise':
      applyAddNoiseToCanvas(source, target, config);
      return true;
    case 'filter-reduce-noise':
      applyReduceNoiseToCanvas(source, target, config);
      return true;
    case 'filter-median':
      applyMedianToCanvas(source, target, config);
      return true;
    case 'filter-emboss':
      applyEmbossToCanvas(source, target, config);
      return true;
    case 'filter-find-edges':
      applyFindEdgesToCanvas(source, target, config);
      return true;
    case 'filter-posterize':
      applyPosterizeToCanvas(source, target, config);
      return true;
    case 'filter-threshold':
      applyThresholdToCanvas(source, target, config);
      return true;
    case 'filter-pixelate':
      applyPixelateToCanvas(source, target, config);
      return true;
    case 'filter-lens-correction':
      applyLensCorrectionToCanvas(source, target, config);
      return true;
    case 'filter-vignette':
      applyVignetteToCanvas(source, target, config);
      return true;
    case 'filter-chromatic-aberration':
      applyChromaticAberrationToCanvas(source, target, config);
      return true;
    case 'filter-twirl':
      applyTwirlToCanvas(source, target, config);
      return true;
    case 'filter-pinch':
      applyPinchToCanvas(source, target, config);
      return true;
    default:
      return false;
  }
}

/** Build a typed `AdjustmentConfig` from a kind discriminator and
 *  its matching `*Value`. The per-kind switch lets TypeScript prove
 *  the value's shape matches the kind, replacing the `kind:` +
 *  spread + double-cast pattern callers would otherwise need at
 *  every config-construction site. */
export type AdjustmentValueByKind = {
  'black-white': BlackAndWhiteValue;
  'brightness-contrast': BrightnessContrastValue;
  'color-balance': ColorBalanceValue;
  curves: CurvesValue;
  'hue-saturation': HueSaturationValue;
  levels: LevelsValue;
  'shadows-highlights': ShadowsHighlightsValue;
  vibrance: VibranceValue;
  'filter-gaussian-blur': GaussianBlurValue;
  'filter-box-blur': BoxBlurValue;
  'filter-motion-blur': MotionBlurValue;
  'filter-radial-blur': RadialBlurValue;
  'filter-unsharp-mask': UnsharpMaskValue;
  'filter-smart-sharpen': SmartSharpenValue;
  'filter-add-noise': AddNoiseValue;
  'filter-reduce-noise': ReduceNoiseValue;
  'filter-median': MedianValue;
  'filter-emboss': EmbossValue;
  'filter-find-edges': FindEdgesValue;
  'filter-posterize': PosterizeValue;
  'filter-threshold': ThresholdValue;
  'filter-pixelate': PixelateValue;
  'filter-lens-correction': LensCorrectionValue;
  'filter-vignette': VignetteValue;
  'filter-chromatic-aberration': ChromaticAberrationValue;
  'filter-twirl': TwirlValue;
  'filter-pinch': PinchValue;
};

export type ImplementedAdjustmentKind = keyof AdjustmentValueByKind;

export function buildAdjustmentConfig<K extends ImplementedAdjustmentKind>(
  kind: K,
  value: AdjustmentValueByKind[K],
): AdjustmentConfig {
  switch (kind) {
    case 'black-white':
      return { kind, ...(value as BlackAndWhiteValue) };
    case 'brightness-contrast':
      return { kind, ...(value as BrightnessContrastValue) };
    case 'color-balance':
      return { kind, ...(value as ColorBalanceValue) };
    case 'curves':
      return { kind, ...(value as CurvesValue) };
    case 'hue-saturation':
      return { kind, ...(value as HueSaturationValue) };
    case 'levels':
      return { kind, ...(value as LevelsValue) };
    case 'shadows-highlights':
      return { kind, ...(value as ShadowsHighlightsValue) };
    case 'vibrance':
      return { kind, ...(value as VibranceValue) };
    case 'filter-gaussian-blur':
      return { kind, ...(value as GaussianBlurValue) };
    case 'filter-box-blur':
      return { kind, ...(value as BoxBlurValue) };
    case 'filter-motion-blur':
      return { kind, ...(value as MotionBlurValue) };
    case 'filter-radial-blur':
      return { kind, ...(value as RadialBlurValue) };
    case 'filter-unsharp-mask':
      return { kind, ...(value as UnsharpMaskValue) };
    case 'filter-smart-sharpen':
      return { kind, ...(value as SmartSharpenValue) };
    case 'filter-add-noise':
      return { kind, ...(value as AddNoiseValue) };
    case 'filter-reduce-noise':
      return { kind, ...(value as ReduceNoiseValue) };
    case 'filter-median':
      return { kind, ...(value as MedianValue) };
    case 'filter-emboss':
      return { kind, ...(value as EmbossValue) };
    case 'filter-find-edges':
      return { kind, ...(value as FindEdgesValue) };
    case 'filter-posterize':
      return { kind, ...(value as PosterizeValue) };
    case 'filter-threshold':
      return { kind, ...(value as ThresholdValue) };
    case 'filter-pixelate':
      return { kind, ...(value as PixelateValue) };
    case 'filter-lens-correction':
      return { kind, ...(value as LensCorrectionValue) };
    case 'filter-vignette':
      return { kind, ...(value as VignetteValue) };
    case 'filter-chromatic-aberration':
      return { kind, ...(value as ChromaticAberrationValue) };
    case 'filter-twirl':
      return { kind, ...(value as TwirlValue) };
    case 'filter-pinch':
      return { kind, ...(value as PinchValue) };
  }
  // Exhaustiveness — unreachable if `K extends ImplementedAdjustmentKind`.
  const _exhaustive: never = kind;
  return _exhaustive;
}

/** True when the config's kind has a runtime dispatch in
 *  `applyAdjustmentToCanvas`. Picker / creation paths should gate
 *  on this so a placeholder kind never lands as a real
 *  adjustment-layer config. */
export function isImplementedKind(kind: AdjustmentConfig['kind']): boolean {
  switch (kind) {
    case 'black-white':
    case 'brightness-contrast':
    case 'color-balance':
    case 'curves':
    case 'hue-saturation':
    case 'levels':
    case 'shadows-highlights':
    case 'vibrance':
    case 'filter-gaussian-blur':
    case 'filter-box-blur':
    case 'filter-motion-blur':
    case 'filter-radial-blur':
    case 'filter-unsharp-mask':
    case 'filter-smart-sharpen':
    case 'filter-add-noise':
    case 'filter-reduce-noise':
    case 'filter-median':
    case 'filter-emboss':
    case 'filter-find-edges':
    case 'filter-posterize':
    case 'filter-threshold':
    case 'filter-pixelate':
    case 'filter-lens-correction':
    case 'filter-vignette':
    case 'filter-chromatic-aberration':
    case 'filter-twirl':
    case 'filter-pinch':
      return true;
    default:
      return false;
  }
}
